/**
 * Auth state shim.
 *
 * The legacy bcrypt+cookie flow is gone — Azure AD owns identity now.
 * This module keeps the shape `App.tsx` already consumed:
 *
 *  - `useCurrentUser()` returns `{ state, signOut }` where `state` is
 *    `loading | unauthenticated | authenticated | error`.
 *
 * The auth values come from MSAL (`useAuth()`) plus a single
 * `/api/auth/me` round-trip that lets the backend tell the frontend the
 * user's role (lpo / admin / superuser). 401/403 surfaces as an explicit
 * `noAccess` shape so the LoginPage can show a "request access" hint.
 */
import { useEffect, useState } from 'react';

import { getActiveIdentity, useAuth } from '../auth/AuthContext';
import { ApiError } from '../api';

export type CurrentUser = {
  id: number;
  email: string;
  full_name: string;
  role: 'lpo' | 'admin' | 'superuser';
};

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'noAccess'; email: string; detail: string }
  | { status: 'authenticated'; user: CurrentUser }
  | { status: 'error'; error: string };

async function fetchMe(): Promise<CurrentUser> {
  const identity = getActiveIdentity();
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (identity) {
    headers['X-User-Email'] = identity.email;
    headers['X-User-Name'] = identity.name;
  }
  const r = await fetch('/api/auth/me', { headers });
  if (!r.ok) {
    const body = await r.text();
    throw new ApiError(r.status, body);
  }
  return (await r.json()) as CurrentUser;
}

export function useCurrentUser(): {
  state: AuthState;
  signOut: () => Promise<void>;
} {
  const auth = useAuth();
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (auth.isLoading) {
      setState({ status: 'loading' });
      return;
    }
    if (!auth.isAuthenticated || !auth.identity) {
      setState({ status: 'unauthenticated' });
      return;
    }
    (async () => {
      try {
        const user = await fetchMe();
        if (!cancelled) setState({ status: 'authenticated', user });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) {
          setState({
            status: 'noAccess',
            email: auth.identity?.email ?? '',
            detail:
              (typeof e.detail === 'object' && e.detail !== null && 'detail' in (e.detail as object))
                ? String((e.detail as { detail: string }).detail)
                : String(e.detail || e.message),
          });
          return;
        }
        if (e instanceof ApiError && e.status === 401) {
          setState({ status: 'unauthenticated' });
          return;
        }
        setState({
          status: 'error',
          error: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.isLoading, auth.identity]);

  return { state, signOut: auth.logout };
}
